<script setup>
import { consentBannerReopen } from '@/lib/consent'
</script>

<template>
  <div class="prose prose-lg max-w-none">
    <p class="text-gray-600 mb-6">Last updated: 21.10.2026</p>

    <p class="text-gray-700 mb-8">
      This Privacy Policy explains how Bindle Resources Ltd processes personal data relating to tutors.
    </p>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. Data Controller</h2>
      <p class="text-gray-700 mb-4">
        Bindle Resources Ltd is the data controller. Contact: privacy@bindle.co.uk
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Personal Data We Collect</h2>
      <p class="text-gray-700 mb-4">
        This may include identity data, contact data, ID documents, DBS details, payment information, platform usage data, communications, and Tutor Content metadata.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Data</h2>
      <p class="text-gray-700 mb-4">
        Data is used to onboard tutors, comply with safeguarding obligations, process payments, operate and improve the platform, and ensure platform security.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. Legal Bases</h2>
      <p class="text-gray-700 mb-4">
        Processing is based on contract performance, legal obligations, legitimate interests, and consent where required.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. Data Sharing</h2>
      <p class="text-gray-700 mb-4">
        Data may be shared with payment processors, verification providers, regulators, and authorities as required by law. We do not sell personal data.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
      <p class="text-gray-700 mb-4">
        Data is retained only as long as necessary for contractual and legal purposes.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
      <p class="text-gray-700 mb-4">
        Tutors may request access, correction, deletion, restriction, or portability of their data and may complain to the ICO.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Data Security</h2>
      <p class="text-gray-700 mb-4">
        Appropriate technical and organisational measures are used to protect data.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">9. International Transfers</h2>
      <p class="text-gray-700 mb-4">
        Where data is processed outside the UK, appropriate safeguards are applied.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">10. Cookies and Analytics</h2>
      <p class="text-gray-700 mb-4">
        This section applies to everyone who visits Bindle. We use Google Analytics, Lucky Orange, PostHog and the Meta Pixel to understand how the platform is used and to improve it. Lucky Orange and PostHog record how pages are used, such as clicks and scrolling, with anything typed into a form hidden. None of them is loaded until you accept them in the cookie banner, and each sets cookies or similar storage only once you have. If you reject them, we store only your choice, so we do not ask again on every page.
      </p>
      <p class="text-gray-700 mb-4">
        Separately, if you have an account, our server tells PostHog about the main steps of your use of Bindle: signing up, confirming your email address, requesting and starting lessons with a tutor, lessons taking place and being paid for, and an arrangement ending. These records carry your account number, amounts and outcomes, never your name or contact details, and set nothing on your device. We do this in our legitimate interest in understanding where the service works and where people give up, so we can improve it. You can object to it by writing to us.
      </p>
      <p class="text-gray-700 mb-4">
        You can change your choice at any time from "Cookie settings" in the site's footer, or here.
        <button type="button" class="font-medium text-brand underline" @click="consentBannerReopen">Change cookie settings</button>
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-900 mb-4">11. Updates</h2>
      <p class="text-gray-700 mb-4">
        This policy may be updated from time to time.
      </p>
    </section>
  </div>
</template>
